import React, { useEffect, useState, useMemo } from "react";
import api from "../../../api/axios";
import ArticleToolbar from "./Toolbars/ArticleToolbar";
import AttributeDefaultsSettings from "./Toolbars/AttributeDefaultsSettings";
import ArticleModal from "./Toolbars/ArticleModal";
import ArticleTreeView from "./TreeView/ArticleTreeView";
import ArticleHierarchyTreeView from "./TreeView/ArticleHierarchyTreeView";
import ArticleDetails from "./Details/ArticleDetails";
import ArticleImageGallery from "../../Common/ArticleImageGallery";
import ArticleExcelImporter from "./ExcelExportImport/ArticleExcelImporter";
import { exportArticlesToExcel } from "./ExcelExportImport/ArticleExcelIO";
import { useCurrentTenant } from "../../../hooks/useCurrentTenant";
import DeletionWarningBanner from "../../Common/DeletionWarningBanner";
import ArticleCatalog from "./Catalog/ArticleCatalog";
import "./ArticleManagement.css";

const ArticleManagement = ({ projectId, onProjectRefresh }) => {
    const currentTenantId = useCurrentTenant();

    const [groupBy, setGroupBy] = useState("manufacturer");
    const [search, setSearch] = useState("");
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [articles, setArticles] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingArticle, setEditingArticle] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [deletionWarnings, setDeletionWarnings] = useState([]);
    const [refreshingId, setRefreshingId] = useState(null);
    const [view, setView] = useState("tree");
    const [hierarchies, setHierarchies] = useState([]);
    const [activeHierarchy, setActiveHierarchy] = useState(null);

    // Use Effects
    useEffect(() => {
        fetchArticles();
        fetchActiveHierarchy();
    }, []);

    // Tenant changed (e.g. switched on the Home page) — drop any selection
    // that no longer belongs to this tenant's view.
    useEffect(() => {
        setSelectedIds(new Set());
        setSelectedArticle(null);
    }, [currentTenantId]);

    const withImpact = (results) =>
        (Array.isArray(results) ? results : [results]).filter(
            (r) => r?.affectedScenarios?.length > 0
        );


    const fetchArticles = async () => {
        try {
            const [
                subGroups,
                enclosures,
                rails,
                ducts,
                // assemblies,
                // modules
            ] = await Promise.all([
                api.get("/subgroup"),
                api.get("/enclosure"),
                api.get("/rail"),
                api.get("/duct"),
                // api.get("/assembly"),
                // api.get("/module"),
            ]);

            const parts = [
                ...subGroups.data,
                ...enclosures.data,
                ...rails.data,
                ...ducts.data,
            ];


            setArticles([
                ...parts,
                // ...assemblies.data,
                // ...modules.data,
            ]);

            setSelectedArticle((prev) =>
                prev ? parts.find((a) => a.id === prev.id) ?? null : prev
            );

        } catch (err) {
            console.error(err);
        }
    };
    const fetchActiveHierarchy = async () => {
        try {
            const { data } = await api.get("/tenantfilterhierarchy", {
                headers: {
                    "X-Tenant": currentTenantId,
                },
            });

            setHierarchies(data);

            const active =
                data.find((h) => h.isActive) ??
                data[0] ??
                null;

            setActiveHierarchy(active);
        } catch (err) {
            console.error("Failed to load hierarchies", err);
        }
    };

    // Only show articles that belong to the currently selected tenant.
    const tenantArticles = useMemo(
        () => articles.filter((a) => a.tenantId === currentTenantId),
        [articles, currentTenantId]
    );

    // Functions
    const handleAdd = () => {
        setEditingArticle(null);
        setModalOpen(true);
    };

    const handleImport = () => {
        document.getElementById("article-excel-importer-trigger").click();
    };

    const handleExport = () => {
        exportArticlesToExcel(tenantArticles, "parts_library");
    };

    // Same per-type dispatch as handleDelete, just looped over the checked articles.
    const deleteOne = async (article) => {
        switch (article.partType) {
            case "SubGroup":
                return api.delete(`/subgroup/${article.id}`);
            case "Enclosure":
                return api.delete(`/enclosure/${article.id}`);
            case "Rail":
                return api.delete(`/rail/${article.id}`);
            case "Duct":
                return api.delete(`/duct/${article.id}`);
            default:
                throw new Error(`Unsupported type for article ${article.id}`);
        }
    };

    // ---- bulk delete: switch to the bulk endpoint so we get one batch result back ----
    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;

        const toDelete = tenantArticles.filter((a) => selectedIds.has(a.id));

        const confirmed = window.confirm(
            `Delete ${toDelete.length} selected article(s)?`
        );
        if (!confirmed) return;

        // Group by endpoint, since bulk-delete is per part-type route
        const byType = toDelete.reduce((acc, a) => {
            (acc[a.partType] ??= []).push(a.id);
            return acc;
        }, {});

        const endpointFor = {
            SubGroup: "/subgroup/bulk-delete",
            Enclosure: "/enclosure/bulk-delete",
            Rail: "/rail/bulk-delete",
            Duct: "/duct/bulk-delete",
        };

        const results = await Promise.allSettled(
            Object.entries(byType).map(([partType, ids]) =>
                api.post(endpointFor[partType], { ids })
            )
        );

        const failed = results.filter((r) => r.status === "rejected");
        if (failed.length > 0) {
            console.error(`${failed.length} bulk-delete request(s) failed`, failed);
        }

        const allWarnings = results
            .filter((r) => r.status === "fulfilled")
            .flatMap((r) => withImpact(r.value.data));

        setSelectedIds(new Set());

        if (selectedArticle && selectedIds.has(selectedArticle.id)) {
            setSelectedArticle(null);
        }

        fetchArticles();
        setDeletionWarnings(allWarnings);
    };

    const handleSave = async (article) => {
        try {
            const isUpdate = !!article.id;
            let response;

            if (isUpdate) {
                switch (article.partType) {
                    case "SubGroup":
                        response = await api.put(`/subgroup/${article.id}`, article);
                        break;
                    case "Enclosure":
                        response = await api.put(`/enclosure/${article.id}`, article);
                        break;
                    case "Rail":
                        response = await api.put(`/rail/${article.id}`, article);
                        break;
                    case "Duct":
                        response = await api.put(`/duct/${article.id}`, article);
                        break;
                    default:
                        throw new Error("Unsupported part type");
                }
            } else {
                switch (article.recordType) {
                    case "Part":
                        switch (article.partType) {
                            case "SubGroup":
                                response = await api.post("/subgroup", article);
                                break;
                            case "Enclosure":
                                response = await api.post("/enclosure", article);
                                break;
                            case "Rail":
                                response = await api.post("/rail", article);
                                break;
                            case "Duct":
                                response = await api.post("/duct", article);
                                break;
                            default:
                                throw new Error("Unsupported part type");
                        }
                        break;

                    case "Assembly":
                        response = await api.post("/assembly", article);
                        break;

                    case "Module":
                        response = await api.post("/module", article);
                        break;

                    default:
                        throw new Error("Unsupported record type");
                }
            }

            fetchArticles();
            return response?.data;

        } catch (err) {
            console.error("Failed to save article", err);
            throw err;
        }
    };

    const handleDelete = async (article) => {

        const confirmed = window.confirm(
            `Delete article "${article.name}"?`
        );

        if (!confirmed) {
            return;
        }
        try {
            let result = null;
            switch (article.partType) {
                case "SubGroup":
                    ({ data: result } = await api.delete(`/subgroup/${article.id}`));
                    break;
                case "Enclosure":
                    ({ data: result } = await api.delete(`/enclosure/${article.id}`));
                    break;
                case "Rail":
                    ({ data: result } = await api.delete(`/rail/${article.id}`));
                    break;
                case "Duct":
                    ({ data: result } = await api.delete(`/duct/${article.id}`));
                    break;
                default:
                    throw new Error("Unsupported type");
            }

            setSelectedArticle(null);

            fetchArticles();
            setDeletionWarnings(withImpact(result));

        } catch (err) {
            console.error(err);
        }
    };

    const canDelete = (article, tenantId) =>
        article.ownership === "Tenant"
            ? article.tenantId === tenantId
            : tenantId === "Innovation_Projects";

    const renderBadge = (article) => {
        const imported = article.ownership === "Tenant" && !!article.sourceArticleId;
        if (imported) return <span className="badge badge-imported" />;
        if (article.isGlobal) return <span className="badge badge-global" />;
        return null;
    };

    const selectableTenantArticles = useMemo(
        () => tenantArticles.filter((a) => canDelete(a, currentTenantId)),
        [tenantArticles, currentTenantId]
    );

    const allSelected =
        selectableTenantArticles.length > 0 &&
        selectableTenantArticles.every((a) => selectedIds.has(a.id));

    const handleToggleSelectAll = (checked) => {
        if (checked) {
            setSelectedIds(new Set(selectableTenantArticles.map((a) => a.id)));
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleRefreshFromGlobal = async (article) => {
        if (!article.sourceArticleId) return;

        setRefreshingId(article.id);

        try {
            await api.post("/article/import", {
                sourceArticleIds: [article.sourceArticleId],
            });

            await fetchArticles();
        } catch (err) {
            console.error("Failed to refresh from Global Library", err);
        } finally {
            setRefreshingId(null);
        }
    };

    const handleHierarchyChange = async (hierarchyId) => {
        try {
            await api.post(
                `/tenantfilterhierarchy/${hierarchyId}/activate`,
                null,
                {
                    headers: {
                        "X-Tenant": currentTenantId,
                    },
                }
            );

            const activated = hierarchies.find(
                (h) => h.id === hierarchyId
            );

            if (activated) {
                setActiveHierarchy(activated);
            }

            await fetchActiveHierarchy();
        } catch (err) {
            console.error("Failed to activate hierarchy", err);
        }
    };


    return (
        <div>
            <ArticleToolbar
                search={search}
                setSearch={setSearch}
                hierarchy={activeHierarchy}
                hierarchies={hierarchies}
                onHierarchyChange={handleHierarchyChange}
                view={view}
                setView={setView}
                selectedCount={selectedIds.size}
                totalSelectableCount={selectableTenantArticles.length}
                allSelected={allSelected}
                onToggleSelectAll={handleToggleSelectAll}
                onAdd={handleAdd}
                onImport={handleImport}
                onExport={handleExport}
                onDeleteSelected={handleBulkDelete}
                onOpenSettings={() => setSettingsOpen(true)}
            />

            <ArticleExcelImporter
                inputId="article-excel-importer-trigger"
                ownership="Tenant"
                tenantId={currentTenantId}
                defaultPartType="SubGroup"
                onImportComplete={fetchArticles}
            />

            <AttributeDefaultsSettings
                isOpen={settingsOpen}
                onClose={() => {
                    setSettingsOpen(false);
                    fetchActiveHierarchy();
                }}
            />
            <p>Articles: {tenantArticles.length}</p>

            <ArticleModal
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setEditingArticle(null);
                }}
                onSave={handleSave}
                editingArticle={editingArticle}
            />
            <div className={`article-layout ${view === "catalog" ? "article-layout-catalog" : "article-layout-tree"}`}>
                <DeletionWarningBanner
                    warnings={deletionWarnings}
                    onDismiss={() => setDeletionWarnings([])}
                />

                {view === "tree" ? (
                    <div className="am-list-pane">
                        <ArticleHierarchyTreeView
                            articles={tenantArticles}
                            hierarchy={
                                activeHierarchy?.levels ?? [
                                    "recordType",
                                    "name",
                                    "type",
                                    "manufacturerShortName"
                                ]
                            }
                            search={search}
                            selectedArticle={selectedArticle}
                            onSelect={setSelectedArticle}
                            checkedIds={selectedIds}
                            onCheckedIdsChange={setSelectedIds}
                            canCheck={(article) => canDelete(article, currentTenantId)}
                            checkboxDisabledTitle="Kein Löschrecht für diesen Artikel"
                            renderBadge={renderBadge}
                        />
                    </div>
                ) : (
                    <div className="am-catalog-pane">
                        <ArticleCatalog
                            articles={tenantArticles}
                            search={search}
                            selectedArticle={selectedArticle}
                            onSelect={setSelectedArticle}
                            checkedIds={selectedIds}
                            onCheckedIdsChange={setSelectedIds}
                            canCheck={(article) => canDelete(article, currentTenantId)}
                            checkboxDisabledTitle="Kein Löschrecht für diesen Artikel"
                            renderBadge={renderBadge}
                        />
                    </div>
                )}

                <div className="article-content">
                    <ArticleDetails
                        article={selectedArticle}
                        onEdit={(article) => {
                            setEditingArticle(article);
                            setModalOpen(true);
                        }}
                        onDelete={handleDelete}
                        onRefreshFromGlobal={handleRefreshFromGlobal}
                        isRefreshing={refreshingId === selectedArticle?.id}
                    />

                    <ArticleImageGallery article={selectedArticle} variant="large" />
                </div>
            </div>

        </div>
    );
};

export default ArticleManagement;